import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class OptimizedImageService {
	private readonly imageCache = new Map<string, string>();
	private readonly loadingImages = new Set<string>();
	private readonly loadedImages = new BehaviorSubject<Set<string>>(new Set());

	/**
	 * Load image with optimization
	 */
	async loadImage(
		src: string,
		options?: {
			quality?: number;
			format?: 'webp' | 'avif' | 'jpeg' | 'png';
			width?: number;
			height?: number;
		}
	): Promise<string> {
		// Check cache first
		if (this.imageCache.has(src)) {
			return this.imageCache.get(src)!;
		}

		// Check if already loading
		if (this.loadingImages.has(src)) {
			return new Promise((resolve) => {
				const subscription = this.loadedImages.subscribe((loaded) => {
					if (loaded.has(src)) {
						subscription.unsubscribe();
						resolve(this.imageCache.get(src)!);
					}
				});
			});
		}

		this.loadingImages.add(src);

		try {
			const optimizedSrc = this.optimizeImageSrc(src, options);
			const imageUrl = await this.preloadImage(optimizedSrc);

			this.imageCache.set(src, imageUrl);
			this.loadingImages.delete(src);

			const currentLoaded = this.loadedImages.value;
			currentLoaded.add(src);
			this.loadedImages.next(currentLoaded);

			return imageUrl;
		} catch (error) {
			this.loadingImages.delete(src);
			console.error('Failed to load image:', src, error);
			throw error;
		}
	}

	/**
	 * Optimize image source based on browser capabilities
	 */
	private optimizeImageSrc(
		src: string,
		options?: {
			quality?: number;
			format?: 'webp' | 'avif' | 'jpeg' | 'png';
			width?: number;
			height?: number;
		}
	): string {
		// Check browser support for modern formats
		const canvas = document.createElement('canvas');
		const supportsWebP =
			canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
		const supportsAVIF =
			canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;

		// Determine best format
		let format = options?.format ?? 'jpeg';
		if (supportsAVIF && !options?.format) {
			format = 'avif';
		} else if (supportsWebP && !options?.format) {
			format = 'webp';
		}

		// Add format extension if not present
		const baseSrc = src.split('?')[0];
		const extension = baseSrc.split('.').pop()?.toLowerCase();

		if (!['webp', 'avif', 'jpeg', 'jpg', 'png'].includes(extension ?? '')) {
			return src; // Return original if we can't determine format
		}

		// Create optimized URL
		const params = new URLSearchParams();
		if (options?.quality) {
			params.set('q', options.quality.toString());
		}
		if (options?.width) {
			params.set('w', options.width.toString());
		}
		if (options?.height) {
			params.set('h', options.height.toString());
		}
		params.set('f', format);

		return `${baseSrc}?${params.toString()}`;
	}

	/**
	 * Preload image and return promise
	 */
	private preloadImage(src: string): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();

			// Use Intersection Observer for lazy loading
			if ('IntersectionObserver' in globalThis) {
				const observer = new IntersectionObserver((entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							img.src = src;
							observer.disconnect();
						}
					});
				});

				// Create a placeholder element to observe
				const placeholder = document.createElement('div');
				placeholder.style.position = 'absolute';
				placeholder.style.top = '-1000px';
				placeholder.style.width = '1px';
				placeholder.style.height = '1px';
				document.body.appendChild(placeholder);
				observer.observe(placeholder);

				img.onload = () => {
					document.body.removeChild(placeholder);
					resolve(src);
				};
				img.onerror = () => {
					document.body.removeChild(placeholder);
					reject(new Error(`Failed to load image: ${src}`));
				};
			} else {
				// Fallback for browsers without Intersection Observer
				img.src = src;
				img.onload = () => resolve(src);
				img.onerror = () =>
					reject(new Error(`Failed to load image: ${src}`));
			}
		});
	}

	/**
	 * Get cached image
	 */
	getCachedImage(src: string): string | null {
		return this.imageCache.get(src) ?? null;
	}

	/**
	 * Check if image is loaded
	 */
	isImageLoaded(src: string): boolean {
		return this.loadedImages.value.has(src);
	}

	/**
	 * Clear image cache
	 */
	clearCache(): void {
		this.imageCache.clear();
		this.loadingImages.clear();
		this.loadedImages.next(new Set());
	}

	/**
	 * Get cache statistics
	 */
	getCacheStats(): any {
		return {
			cachedImages: this.imageCache.size,
			loadingImages: this.loadingImages.size,
			loadedImages: this.loadedImages.value.size
		};
	}
}
